import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";

const STORAGE_KEY = "wua:cookie-consent";
const CONSENT_EVENT = "wua:cookie-consent-changed";

// Public helper used elsewhere in the app (e.g. footer link that lets a
// user re-open their cookie choice). Reads current status without
// triggering a re-render loop.
export function getCookieConsent() {
  try {
    return localStorage.getItem(STORAGE_KEY);   // "accepted" | "rejected" | null
  } catch {
    return null;
  }
}

function applyConsent(choice) {
  try {
    localStorage.setItem(STORAGE_KEY, choice);
  } catch {
    /* private-mode Safari, ignore */
  }
  // PostHog is loaded in index.html as `window.posthog`. Toggle capture
  // based on the choice so we never send analytics when the user opted
  // out. Guarded because it's not present in dev SSR.
  try {
    if (typeof window !== "undefined" && window.posthog) {
      if (choice === "accepted") window.posthog.opt_in_capturing();
      else window.posthog.opt_out_capturing();
    }
  } catch {
    /* posthog stub may throw before init — safe to ignore */
  }
  // Let other components react (e.g. show a "cookies rejected" toast, or
  // re-render the manage-cookies link in the footer).
  try {
    window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: choice }));
  } catch {}
}

export default function CookieBanner() {
  const [choice, setChoice] = useState("pending");

  // Read the current consent on mount. We intentionally do NOT show the
  // banner during SSR / first paint — that would flash on every route
  // change. Wait a tick so the initial route paints first.
  useEffect(() => {
    const existing = getCookieConsent();
    if (existing) {
      setChoice(existing);
      // Re-apply on every mount so a returning user's PostHog opt-out
      // survives page loads even if their cookies were cleared elsewhere.
      applyConsent(existing);
      return;
    }
    // Delay slightly so the banner slides in after the page renders,
    // not on top of the loading state.
    const t = setTimeout(() => setChoice("show"), 500);
    return () => clearTimeout(t);
  }, []);

  // Listen for cross-tab / manual re-open events (e.g. footer link).
  useEffect(() => {
    const onChange = (e) => setChoice(e.detail || getCookieConsent() || "show");
    window.addEventListener(CONSENT_EVENT, onChange);
    return () => window.removeEventListener(CONSENT_EVENT, onChange);
  }, []);

  const accept = () => {
    applyConsent("accepted");
    setChoice("accepted");
  };
  const reject = () => {
    applyConsent("rejected");
    setChoice("rejected");
  };

  if (choice !== "show") return null;

  return (
    <div
      data-testid="cookie-banner"
      className="fixed bottom-4 left-4 right-4 sm:left-6 sm:right-6 md:left-auto md:right-6 md:max-w-md z-[70] rounded-2xl bg-[#0F172A] text-white shadow-[0_20px_60px_-15px_rgba(0,0,0,0.5)] p-5 sm:p-6 animate-in slide-in-from-bottom-4"
      role="dialog"
      aria-labelledby="cookie-banner-title"
    >
      <div className="flex items-start gap-3">
        <div className="shrink-0 h-9 w-9 rounded-full bg-white/10 flex items-center justify-center">
          <Cookie className="h-4 w-4 text-amber-300" />
        </div>
        <div className="flex-1 min-w-0">
          <div
            id="cookie-banner-title"
            className="font-editorial text-lg leading-tight"
          >
            Cookies, plainly.
          </div>
          <p className="mt-1.5 text-[13px] leading-relaxed text-stone-300">
            We use essential cookies to keep you signed in and to remember your
            currency. Anonymous analytics help us decide what to build next —
            we never sell your data and never run advertising cookies.{" "}
            <Link
              to="/legal/cookies"
              data-testid="cookie-banner-policy-link"
              className="underline underline-offset-2 hover:text-white"
            >
              Read the full policy
            </Link>
            .
          </p>
        </div>
        <button
          type="button"
          onClick={reject}
          aria-label="Dismiss and reject non-essential cookies"
          data-testid="cookie-banner-dismiss"
          className="shrink-0 h-7 w-7 -mt-1 -mr-1 rounded-full hover:bg-white/10 flex items-center justify-center text-stone-400 hover:text-white transition-colors"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className="mt-4 flex flex-col sm:flex-row gap-2">
        <button
          type="button"
          onClick={reject}
          data-testid="cookie-banner-reject"
          className="flex-1 px-4 py-2 rounded-md border border-white/20 text-sm hover:bg-white/10 transition-colors font-underground-sc tracking-wider text-[11px]"
        >
          Reject non-essential
        </button>
        <button
          type="button"
          onClick={accept}
          data-testid="cookie-banner-accept"
          className="flex-1 px-4 py-2 rounded-md bg-white text-[#0F172A] text-sm hover:bg-stone-100 transition-colors font-underground-sc tracking-wider text-[11px] font-semibold"
        >
          Accept all
        </button>
      </div>
    </div>
  );
}
